// @ts-nocheck

import { useState, useEffect, useRef } from "react";
import { getSafeImageUrl, trackImageEvent, DEFAULT_IMAGES, toSupabaseRenderUrl } from "@/utils/imageUtils";
import { Skeleton } from "@/components/ui/skeleton";

interface SafeImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string | null | undefined;
  alt: string;
  fallbackSrc?: string;
  logErrorDetails?: string;
  aspectRatio?: string;
  className?: string;
  containerClassName?: string;
  showSkeleton?: boolean;
  objectFit?: 'cover' | 'contain' | 'fill' | 'scale-down' | 'none';
  quality?: 'low' | 'medium' | 'high';
  sizes?: string;
  srcSet?: string;
  isPriority?: boolean;
  generateAlternates?: boolean;
  width?: number;
  height?: number;
}

export function SafeImage({
  src,
  alt,
  fallbackSrc = DEFAULT_IMAGES.GENERIC,
  logErrorDetails,
  aspectRatio,
  className = "",
  containerClassName = "",
  showSkeleton = true,
  objectFit = 'contain',
  quality = 'medium',
  sizes,
  srcSet,
  isPriority = false,
  generateAlternates = false,
  width,
  height,
  ...props
}: SafeImageProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(() => getSafeImageUrl(src, fallbackSrc));
  const [isInView, setIsInView] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Keep currentSrc in sync with incoming src
  useEffect(() => {
    const next = getSafeImageUrl(src, fallbackSrc);
    setCurrentSrc(next);
    setHasError(false);
    setIsLoading(true);
  }, [src, fallbackSrc]);

  // IntersectionObserver to delay actual image fetch until in view
  useEffect(() => {
    if (!containerRef.current) return;
    if (typeof window === 'undefined' || 'IntersectionObserver' in window === false) {
      setIsInView(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
          break;
        }
      }
    }, { root: null, rootMargin: '200px', threshold: 0.01 });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);
  
  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.error(`Image failed to load: ${src || 'undefined source'} (${logErrorDetails || 'no details'})`);
    // Track the error event with the original src
    trackImageEvent('error', src, logErrorDetails);
    // If original URL is Supabase storage object, try render endpoint once without size params
    if (!hasError && currentSrc && currentSrc.includes('/storage/v1/object/public/')) {
      const raw = toSupabaseRenderUrl(currentSrc, {});
      if (raw && raw !== currentSrc) {
        setCurrentSrc(raw);
        return;
      }
    }
    // Switch to fallback once to avoid loops and React overriding imperative changes
    if (!hasError && currentSrc !== fallbackSrc) {
      setHasError(true);
      setIsLoading(false);
      setCurrentSrc(fallbackSrc);
    }
  };
  
  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
    // Track the load event
    trackImageEvent('load', currentSrc, logErrorDetails);
  };
  
  // Apply aspect ratio wrapper if needed
  const containerStyle = aspectRatio ? {
    position: 'relative',
    width: '100%',
    paddingBottom: aspectRatio,
    overflow: 'hidden',
  } as React.CSSProperties : {
    position: 'relative',
    width: '100%',
    height: '100%',
  };
  
  const imageStyle = aspectRatio ? {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: objectFit,
  } as React.CSSProperties : {
    width: '100%',
    height: '100%',
    objectFit: objectFit,
  };

  // Quality-based loading strategy
  const isPriorityLoad = isPriority || quality === 'high';
  const loadingStrategy = isPriorityLoad ? 'eager' : 'lazy';

  // Optionally attempt AVIF/WebP alternates by swapping extensions
  const buildAlternateSources = (url: string | null | undefined) => {
    if (!url || !generateAlternates || hasError) return [] as { type: string; srcSet: string }[];
    const lower = url.toLowerCase();
    const hasKnownExt = lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.png');
    if (!hasKnownExt) return [] as { type: string; srcSet: string }[];
    const base = lower.replace(/\.(jpg|jpeg|png)$/i, '');
    return [
      { type: 'image/avif', srcSet: `${base}.avif` },
      { type: 'image/webp', srcSet: `${base}.webp` },
    ];
  };
  const alternateSources = buildAlternateSources(currentSrc);

  // When in view, prefer Supabase render endpoint for optimization if applicable
  const optimizedSrc = isInView
    ? toSupabaseRenderUrl(currentSrc, {
        // Use width hints when available
        quality: quality === 'high' ? 85 : quality === 'low' ? 50 : 70,
        resize: 'contain',
      }) || currentSrc
    : undefined; // avoid setting until in view
  
  // Strip unsupported DOM props before spreading
  const { fetchPriority: _fp, fetchpriority: _fpl, ...restProps } = props as any;

  return (
    <div ref={containerRef} className={`relative ${containerClassName}`} style={containerStyle}>
      {/* Loading skeleton */}
      {isLoading && showSkeleton && (
        <div className="absolute inset-0 z-10 w-full h-full">
          <Skeleton className="w-full h-full bg-gradient-to-br from-brown-100 to-brown-200" />
        </div>
      )}
      
      {/* Error state indicator */}
      {hasError && (
        <div className="absolute top-2 right-2 z-20">
          <div className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
            <span className="text-white text-xs">!</span>
          </div>
        </div>
      )}
      
      {alternateSources.length > 0 ? (
        <picture>
          {alternateSources.map((s) => (
            <source key={s.type} type={s.type} srcSet={s.srcSet} sizes={sizes} />
          ))}
          <img
            src={optimizedSrc || (isInView ? currentSrc : undefined)}
            alt={alt}
            className={`${className} ${hasError ? 'opacity-70' : 'opacity-100'} transition-opacity duration-300`}
            style={imageStyle}
            onError={handleError}
            onLoad={handleLoad}
            loading={loadingStrategy}
            decoding={isPriorityLoad ? 'sync' : 'async'}
            fetchpriority={isPriorityLoad ? 'high' : 'auto'}
            sizes={hasError ? undefined : sizes}
            srcSet={hasError ? undefined : srcSet}
            width={width}
            height={height}
            {...restProps}
          />
        </picture>
      ) : (
        <img
          src={optimizedSrc || (isInView ? currentSrc : undefined)}
          alt={alt}
          className={`${className} ${hasError ? 'opacity-70' : 'opacity-100'} transition-opacity duration-300`}
          style={imageStyle}
          onError={handleError}
          onLoad={handleLoad}
          loading={loadingStrategy}
          decoding={isPriorityLoad ? 'sync' : 'async'}
          fetchpriority={isPriorityLoad ? 'high' : 'auto'}
          sizes={hasError ? undefined : sizes}
          srcSet={hasError ? undefined : srcSet}
          width={width}
          height={height}
          {...restProps}
        />
      )}
    </div>
  );
}